import styles from "./ViewStatisticsDetailedItem.module.scss";
import { IStatisticDetailedItemViewModel } from "../models/statisticDetailedItemViewModel";
import { StatisticsPlanDialog } from "./StatisticsPlanDialog";
import { StatisticDetailedItemType } from "../../../common/enums";
import { StatisticsReservationDialog } from "./StatisticsReservationDialog";
import { StatisticsEventDialog } from "./StatisticsEventDialog";
import { StatisticsInstructorDialog } from "./StatisticsInstructorDialog";
import { StatisticsEmailDialog } from "./StatisticsEmailDialog";
import { StatisticsSmsDialog } from "./StatisticsSmsDialog";
import { StatisticsOrderDialog } from "./StatisticsOrderDialog";
import { StatisticsGymVisitDialog } from "./StatisticsGymVisitDialog";
import { StatisticsDepositTransactionDialog } from "./StatisticsDepositTransactionDialog";
import { StatisticsDepositDebtorDialog } from "./StatisticsDepositDebtorDialog";
import { getStatisticsInstructorDetailedItemGroups } from "../utilities/statisticsUtility";
import { useRef, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

interface IViewStatisticsDetailedItemProps {
  items?: IStatisticDetailedItemViewModel[];
  selectedDetailItemsKey?: string;
}

export const ViewStatisticsDetailedItem = ({
  items,
  selectedDetailItemsKey,
}: IViewStatisticsDetailedItemProps) => {
  const parentRef = useRef<HTMLDivElement>(null);

  if (!items) {
    return;
  }

  const isInstructorVisits =
    !!selectedDetailItemsKey &&
    selectedDetailItemsKey?.startsWith("InstructorVisits_");

  const isInstructorPlans =
    !!selectedDetailItemsKey &&
    selectedDetailItemsKey?.startsWith("InstructorPlans_");

  // Prepare data for virtualization based on the view type
  const itemsToVirtualize = useMemo(() => {
    if (isInstructorVisits) {
      return getStatisticsInstructorDetailedItemGroups(items);
    }
    return items;
  }, [items, isInstructorVisits]);

  // Create virtualizer
  const virtualizer = useVirtualizer({
    count: itemsToVirtualize?.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120, // Estimated height per item
    overscan: 5,
  });

  // Render individual item based on type
  const renderItem = (item: any) => {
    if (isInstructorVisits) {
      return <StatisticsInstructorDialog item={item} />;
    }

    if (isInstructorPlans) {
      return <StatisticsPlanDialog item={item} />;
    }

    switch (item.type) {
      case StatisticDetailedItemType.Reservation:
      case StatisticDetailedItemType.ServiceReservation:
        return <StatisticsReservationDialog item={item} />;
      case StatisticDetailedItemType.Event:
        return <StatisticsEventDialog item={item} />;
      case StatisticDetailedItemType.PlanCount:
        return <StatisticsPlanDialog item={item} />;
      case StatisticDetailedItemType.EmailLog:
        return <StatisticsEmailDialog item={item} />;
      case StatisticDetailedItemType.SmsLog:
        return <StatisticsSmsDialog item={item} />;
      case StatisticDetailedItemType.Order:
        return <StatisticsOrderDialog item={item} />;
      case StatisticDetailedItemType.GymVisit:
        return <StatisticsGymVisitDialog item={item} />;
      case StatisticDetailedItemType.DepositTransaction:
        return <StatisticsDepositTransactionDialog item={item} />;
      case StatisticDetailedItemType.DepositDebtor:
      // "Депозитни задължения" drill-down: the same debtor-style card — the
      // positive balance renders green via the card's amount tone.
      case StatisticDetailedItemType.DepositLiability:
        return <StatisticsDepositDebtorDialog item={item} />;
      default:
        return <StatisticsPlanDialog item={item} />;
    }
  };

  if (!itemsToVirtualize || itemsToVirtualize.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.noResults}>Няма открити резултати.</div>
      </div>
    );
  }

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div ref={parentRef} className={styles.root}>
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualItems.map((virtualRow) => {
          const item = itemsToVirtualize[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
                paddingBottom: "5px",
              }}
            >
              {renderItem(item)}
            </div>
          );
        })}
      </div>
    </div>
  );
};
